import type {FC} from 'react';
import {useMemo} from 'react';
import type {
  GridColDef,
  GridRowClassNameParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {DataGrid, Paper} from '@wellstone-solutions/web';
import {getSortModel} from '../../utils';

type Props = {
  testId: string;
  columns: GridColDef[];
  rows: ReadonlyArray<{id: string | number} & Record<string, unknown>>;
  rowCount: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  ordering: string;
  isMobile: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onRowClick?: (params: {row: unknown}) => void;
  noRowsText?: string;
};

export const PaginatedListGrid: FC<Props> = ({
  testId,
  columns,
  rows,
  rowCount,
  isLoading,
  page,
  pageSize,
  ordering,
  isMobile,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  onRowClick,
  noRowsText,
}) => {
  // Controlled from the URL, so a sort hidden with its column returns with it
  const sortModel = useMemo(
    () =>
      getSortModel(ordering).filter(({field}) =>
        columns.some((column) => column.field === field),
      ),
    [ordering, columns],
  );

  return (
    <Paper elevation={1}>
      <DataGrid
        data-testid={testId}
        autoHeight
        variant="striped"
        density="comfortable"
        columns={columns}
        loading={isLoading}
        noRowsText={noRowsText}
        rows={rows}
        rowCount={rowCount}
        paginationMode="server"
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        sortModel={sortModel}
        page={page - 1}
        pageSize={pageSize}
        onPageChange={(newPage: number) => onPageChange(newPage + 1)}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={(model: GridSortModel) => {
          // Clicks cycle asc/desc only, so an empty model is the grid dropping
          // a sort on a hidden column; the URL keeps it for the server
          if (model.length > 0) {
            onSortModelChange(model);
          }
        }}
        onRowClick={onRowClick}
        {...(onRowClick && {
          sx: {'& .MuiDataGrid-cell': {cursor: 'pointer'}},
        })}
        {...(isMobile && {
          headerHeight: 0,
          getRowHeight: () => 100,
          getRowClassName: (params: GridRowClassNameParams) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even',
        })}
      />
    </Paper>
  );
};
